import { useCallback, useState } from "react";

/** Режим отображения дерева задач на экране «Задачи». */
export type TaskViewMode = "list" | "tiles";

/** Ключ `localStorage` для режима вида задач. */
export const TASKS_VIEW_MODE_STORAGE_KEY = "tasks-view-mode";

const DEFAULT_VIEW_MODE: TaskViewMode = "tiles";

function isTaskViewMode(value: string | null): value is TaskViewMode {
    return value === "list" || value === "tiles";
}

function readStoredViewMode(): TaskViewMode {
    try {
        const stored = globalThis.localStorage?.getItem(TASKS_VIEW_MODE_STORAGE_KEY) ?? null;
        if (isTaskViewMode(stored)) {
            return stored;
        }
    } catch {
        // private mode / недоступный storage — оставляем дефолт
    }

    return DEFAULT_VIEW_MODE;
}

function writeStoredViewMode(mode: TaskViewMode): void {
    try {
        globalThis.localStorage?.setItem(TASKS_VIEW_MODE_STORAGE_KEY, mode);
    } catch {
        // ignore
    }
}

/**
 * Результат {@link useTaskViewMode}.
 */
export interface UseTaskViewModeResult {
    /** Текущий режим отображения. */
    viewMode: TaskViewMode;
    /**
     * Переключает `list` ↔ `tiles` и сохраняет выбор в `localStorage`.
     */
    toggleViewMode: () => void;
    /**
     * Устанавливает режим явно и сохраняет в `localStorage`.
     * @param mode целевой режим
     */
    setViewMode: (mode: TaskViewMode) => void;
}

/**
 * Режим вида экрана «Задачи» с persistence в `localStorage`.
 * @returns текущий режим и методы переключения
 * @default viewMode `"tiles"` (если в storage нет валидного значения)
 */
export function useTaskViewMode(): UseTaskViewModeResult {
    const [viewMode, setViewModeState] = useState<TaskViewMode>(readStoredViewMode);

    const setViewMode = useCallback((mode: TaskViewMode) => {
        setViewModeState(mode);
        writeStoredViewMode(mode);
    }, []);

    const toggleViewMode = useCallback(() => {
        setViewModeState((prev) => {
            const next: TaskViewMode = prev === "tiles" ? "list" : "tiles";
            writeStoredViewMode(next);
            return next;
        });
    }, []);

    return { viewMode, toggleViewMode, setViewMode };
}
